import { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { CheckoutField, DataTestId, FeatureFlag, TextKey } from '@mews-distributor/common';
import { LargeContentContainer } from '@mews-distributor/ui';
import { FeatureFlagService, LocalizedText } from '@mews/framework';
import { Button, Card, Container, Divider, Icon, IconName, Stack, StackItem, Title } from '@optimus-web/core';
import { ViewHeading } from '../../../../components/common';
import { TotalBarWithTaxBreakdown } from '../../../../components/common/TotalBarWithTaxBreakdown/components/TotalBarWithTaxBreakdown';
import { localizeText } from '../../../../modules/globalization/selectors';
import { ApplicationState } from '../../../../types';
import { BookerForm } from '../BookerForm/BookerForm';
import { BookerRadio } from '../BookerRadio/BookerRadio';
import { CheckoutError } from '../CheckoutError';
import { CheckoutInput } from '../CheckoutInput';
import { CheckoutPhoneInput } from '../CheckoutPhoneInput';
import { CheckoutTextarea } from '../CheckoutTextarea';
import { EmailInput } from '../EmailInput';
import { LegalStatements } from '../LegalStatements';
import { MarketingEmails } from '../MarketingEmails';
import { GenericPaymentCardFormSection, PciProxyPaymentCardFormSection } from '../modules';
import { NationalitySelect } from '../NationalitySelect';
import { PaymentCardBanner } from '../PaymentCardBanner';
import { PaymentMethodList } from '../PaymentMethodList';
import { TermsAndConditions } from '../TermsAndConditions';
import { ReservationSms } from '../ReservationSms';
import { setObservabilityUser } from '../../../../observability/actions';

interface BaseCheckoutViewProps {
    enterpriseId: string;
    featureFlagService: FeatureFlagService;
    hasError: boolean;
    hasPaymentCardForm: boolean;
    hasPciProxy: boolean;
    hasSpecialRequestsFieldFeature: boolean;
    isBookerEnabled: boolean;
    isBookerOptionDisabled: boolean;
    isFetching: boolean;
    isImmediatelySettledTotalAmountPositive: boolean;
    localizedGuestContactDetailsFormLabel: string;
    localizedPaymentDetailsFormLabel: string;
    localizedYourContactDetailsFormLabel: string;
    localizedButtonConfirmAndPayNow: string;
    localizedButtonConfirm: string;
    showSmsConsent: boolean;
    onLoaded: () => void;
    onFinishBookingClick: () => Promise<void>;
    scrollTop: () => void;
}

const BaseCheckoutView = ({
    enterpriseId,
    featureFlagService,
    hasError,
    hasPaymentCardForm,
    hasPciProxy,
    hasSpecialRequestsFieldFeature,
    isBookerEnabled,
    isBookerOptionDisabled,
    isFetching,
    isImmediatelySettledTotalAmountPositive,
    localizedGuestContactDetailsFormLabel,
    localizedPaymentDetailsFormLabel,
    localizedYourContactDetailsFormLabel,
    localizedButtonConfirmAndPayNow,
    localizedButtonConfirm,
    showSmsConsent,
    onLoaded,
    onFinishBookingClick,
    scrollTop,
}: BaseCheckoutViewProps) => {
    const [requestMade, setRequestMade] = useState(false);
    const prevRequestMadeRef = useRef(false);

    const dispatch = useDispatch();

    useEffect(() => {
        onLoaded();
    }, [onLoaded]);

    useEffect(() => {
        if (prevRequestMadeRef.current && requestMade) {
            setRequestMade(false);

            if (hasError) {
                scrollTop();
            }
        }
        prevRequestMadeRef.current = requestMade;
    }, [requestMade, hasError, scrollTop]);

    useEffect(() => {
        dispatch(setObservabilityUser(enterpriseId));
    }, [dispatch, enterpriseId]);

    const handleFinishBookingClick = async () => {
        if (!requestMade) {
            setRequestMade(true);
            await onFinishBookingClick?.();
        }
    };

    const errorMessage = hasError ? (
        <Container padding={['200', '0', '0']}>
            <CheckoutError />
        </Container>
    ) : null;

    const GuestEmailFieldHelper = isBookerEnabled ? (
        <LocalizedText textKey={TextKey.WeWillSendConfirmationToThisEmail} />
    ) : null;

    const paymentCardFormSection = hasPaymentCardForm ? (
        <>
            {/* @ts-expect-error $TSFixMeError: JSX element type 'PaymentCardBanner' does not have any construct or call signatures. */}
            <PaymentCardBanner />
            {/* @ts-expect-error $TSFixMeError: JSX element type 'PciProxyPaymentCardFormSection' does not have any construct or call signatures. */}
            {hasPciProxy ? <PciProxyPaymentCardFormSection /> : <GenericPaymentCardFormSection />}
        </>
    ) : null;

    const paymentMethods = isImmediatelySettledTotalAmountPositive ? <PaymentMethodList /> : null;
    const pageTitleTextKey = hasPaymentCardForm ? TextKey.ContactAndPaymentDetails : TextKey.ContactDetails;
    const isPhoneInputPrototypeEnabled = featureFlagService.hasFlag(FeatureFlag.enablePhoneInputPrototype);
    const fallBackPhoneInput = (
        <CheckoutInput
            // @ts-expect-error $TSFixMeError: Type '{ name: CheckoutField; label: Element; autoComplete: string; type: string; }' is not assignable to type 'Intrinsic...
            name={CheckoutField.Telephone}
            label={<LocalizedText textKey={TextKey.PhoneNumber} />}
            autoComplete="tel"
            type="tel"
        />
    );

    return (
        <LargeContentContainer data-test-id={DataTestId.CheckoutView} role="region">
            <Container padding={['0', '0', '300']}>
                <ViewHeading pageTitleTextKey={pageTitleTextKey} />
                {errorMessage}
                <Container padding={['300', '0']}>
                    <Card padding="200">
                        <Stack spacing="300" vertical>
                            {!isBookerOptionDisabled ? <BookerRadio /> : null}

                            <Title level={3} align="center" data-test-id={DataTestId.CheckoutYourDetailsHeading}>
                                <LocalizedText textKey={TextKey.YourDetails} />
                            </Title>
                            {isBookerEnabled ? (
                                <>
                                    <form id="contact-details" aria-label={localizedYourContactDetailsFormLabel}>
                                        <BookerForm />
                                    </form>
                                    <Title
                                        level={3}
                                        align="center"
                                        data-test-id={DataTestId.CheckoutGuestDetailsHeading}
                                    >
                                        <LocalizedText textKey={TextKey.GuestDetails} />
                                    </Title>
                                </>
                            ) : null}
                            <form
                                id="contact-details"
                                aria-label={
                                    isBookerEnabled
                                        ? localizedGuestContactDetailsFormLabel
                                        : localizedYourContactDetailsFormLabel
                                }
                            >
                                <Stack spacing="200" vertical>
                                    <Stack
                                        spacing="200"
                                        distribution="stretch"
                                        largePhoneMax={{
                                            vertical: true,
                                        }}
                                    >
                                        <CheckoutInput
                                            // @ts-expect-error $TSFixMeError: Type '{ name: CheckoutField; autoComplete: string; label: Element; }' is not assignable to type 'IntrinsicAttributes & O...
                                            name={CheckoutField.FirstName}
                                            autoComplete="given-name"
                                            label={<LocalizedText textKey={TextKey.FirstName} />}
                                        />
                                        <CheckoutInput
                                            // @ts-expect-error $TSFixMeError: Type '{ name: CheckoutField; autoComplete: string; label: Element; }' is not assignable to type 'IntrinsicAttributes & O...
                                            name={CheckoutField.LastName}
                                            autoComplete="family-name"
                                            label={<LocalizedText textKey={TextKey.LastName} />}
                                        />
                                    </Stack>
                                    <Stack
                                        spacing="200"
                                        distribution="stretch"
                                        largePhoneMax={{
                                            vertical: true,
                                        }}
                                    >
                                        <EmailInput
                                            name={CheckoutField.Email}
                                            helper={GuestEmailFieldHelper}
                                            optionalFieldHelper={
                                                <Stack spacing="50" alignment="center" itemAlignment="center">
                                                    <Icon value={IconName.Info} variant="basic" size="small" />
                                                    <LocalizedText textKey={TextKey.OptionalGuestEmailHint} />
                                                </Stack>
                                            }
                                        />
                                        {isPhoneInputPrototypeEnabled ? (
                                            <CheckoutPhoneInput
                                                // @ts-expect-error $TSFixMeError: Type 'CheckoutField.Telephone' is not assignable to type 'CheckoutField.BookerEmail | CheckoutField.BookerTelephone'.
                                                name={CheckoutField.Telephone}
                                                label={<LocalizedText textKey={TextKey.PhoneNumber} />}
                                                fallback={fallBackPhoneInput}
                                            />
                                        ) : (
                                            fallBackPhoneInput
                                        )}
                                    </Stack>
                                    <Stack
                                        spacing="200"
                                        distribution="stretch"
                                        largePhoneMax={{
                                            vertical: true,
                                        }}
                                    >
                                        <NationalitySelect />
                                        <div />
                                    </Stack>
                                    {hasSpecialRequestsFieldFeature ? (
                                        <CheckoutTextarea
                                            data-test-id={DataTestId.SpecialRequests}
                                            // @ts-expect-error $TSFixMeError: Type '{ "data-test-id": DataTestId; label: Element; name: CheckoutField; }' is not assignable to type 'IntrinsicAttribut...
                                            label={<LocalizedText textKey={TextKey.BookingNotes} />}
                                            name={CheckoutField.Notes}
                                        />
                                    ) : null}
                                </Stack>
                            </form>
                        </Stack>
                    </Card>
                </Container>
                {hasPaymentCardForm ? (
                    <Container padding={['0', '0', '200']}>
                        <Title level={2} align="center" data-test-id={DataTestId.CheckoutPaymentHeading}>
                            <LocalizedText textKey={TextKey.Payment} />
                        </Title>
                    </Container>
                ) : null}
                <Stack spacing="300" vertical>
                    <Card padding="200">
                        <form id="payment-details" aria-label={localizedPaymentDetailsFormLabel}>
                            <Stack spacing="300" vertical>
                                {paymentCardFormSection}

                                <TotalBarWithTaxBreakdown
                                    // @ts-expect-error $TSFixMeError: Type '{ showSurchargingMessage: true; showRateGroupDescription: true; }' is not assignable to type 'IntrinsicAttributes ...
                                    showSurchargingMessage
                                    showRateGroupDescription
                                    showDccFeeMessage
                                />
                                <Divider />
                                <StackItem>
                                    <TermsAndConditions />
                                    {/* @ts-expect-error Property 'enterpriseId' does not exist on type 'IntrinsicAttributes & Omit<any, string | number | symbol>'. We need to type the inject. */}
                                    {showSmsConsent ? <ReservationSms enterpriseId={enterpriseId} /> : null}
                                    {/* @ts-expect-error Property 'enterpriseId' does not exist on type 'IntrinsicAttributes & Omit<any, string | number | symbol>'. We need to type the inject. */}
                                    <MarketingEmails enterpriseId={enterpriseId} />
                                </StackItem>
                            </Stack>
                        </form>
                    </Card>
                    <Stack spacing="300" itemAlignment="center" vertical distribution="space-between">
                        <Button
                            variant="primary"
                            onClick={handleFinishBookingClick}
                            disabled={isFetching}
                            data-test-id={DataTestId.CheckoutNextButton}
                            aria-label={
                                isImmediatelySettledTotalAmountPositive
                                    ? localizedButtonConfirmAndPayNow
                                    : localizedButtonConfirm
                            }
                            type="submit"
                        >
                            <LocalizedText
                                textKey={
                                    isImmediatelySettledTotalAmountPositive ? TextKey.ConfirmAndPayNow : TextKey.Confirm
                                }
                            />
                        </Button>
                        {paymentMethods}
                    </Stack>
                    <LegalStatements />
                </Stack>
            </Container>
        </LargeContentContainer>
    );
};

export const CheckoutView = connect((state: ApplicationState) => ({
    localizedYourContactDetailsFormLabel: localizeText(TextKey.YourDetails)(state),
    localizedGuestContactDetailsFormLabel: localizeText(TextKey.GuestDetails)(state),
    localizedPaymentDetailsFormLabel: localizeText(TextKey.Payment)(state),
    localizedButtonConfirmAndPayNow: localizeText(TextKey.ConfirmAndPayNow)(state),
    localizedButtonConfirm: localizeText(TextKey.Confirm)(state),
}))(BaseCheckoutView);
